import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Crown, Ticket, CheckCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOOKUP_KEYS = {
  starter: 'sub_starter_monthly',
  pro: 'sub_pro_monthly',
  unlimited: 'sub_unlimited_monthly',
};

export const SubscriptionsModal = ({ open, onOpenChange, user, plans, onSubscribe, isProcessingPayment }) => {
  const isActive = user?.subscription_status === 'active';
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, trial_days, discount_percent, applicable_plans }
  const [checking, setChecking] = useState(false);

  const applyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) {
      toast.error('Enter a promo code');
      return;
    }
    // Pre-validate against Starter plan by default; per-plan applicability is
    // checked again on final checkout.
    setChecking(true);
    try {
      const r = await axios.get(`${API}/promo/preview`, {
        params: { code, lookup_key: LOOKUP_KEYS.starter },
        withCredentials: true,
      });
      setAppliedPromo(r.data);
      toast.success(`${r.data.trial_days}-day free trial code applied`);
    } catch (err) {
      const status = err.response?.status;
      // 400 (plan mismatch) is still fine if the code is valid for other plans —
      // keep it applied and let final subscribe do the strict check.
      if (status === 400 && err.response?.data?.detail?.includes('selected plan')) {
        // Re-query with a broader plan set — try each plan until one succeeds
        for (const lk of Object.values(LOOKUP_KEYS)) {
          try {
            const r2 = await axios.get(`${API}/promo/preview`, {
              params: { code, lookup_key: lk },
              withCredentials: true,
            });
            setAppliedPromo(r2.data);
            toast.success(`${r2.data.trial_days}-day free trial code applied`);
            setChecking(false);
            return;
          } catch { /* try next */ }
        }
      }
      toast.error(err.response?.data?.detail || 'Invalid promo code');
    } finally {
      setChecking(false);
    }
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setPromoInput('');
  };

  const handleSubscribe = (planId) => {
    onSubscribe(planId, appliedPromo?.code || null);
  };

  const promoCoversPlan = (planId) => {
    if (!appliedPromo) return true;
    const lk = LOOKUP_KEYS[planId];
    const list = appliedPromo.applicable_plans || [];
    return list.length === 0 || list.includes(lk);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-lg border-2 border-border max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="subscriptions-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-medium tracking-tight font-serif">Subscription Plans</DialogTitle>
          <DialogDescription className="text-sm text-ash font-sans">
            {isActive ? (
              <>
                You&apos;re subscribed to <span className="font-medium text-gold capitalize">{user.subscription_tier}</span>.
                {user.subscription_expires_at && ` Renews ${new Date(user.subscription_expires_at).toLocaleDateString()}.`}
              </>
            ) : (
              'Sign up to a plan below. Your free 3,000-word trial stays untouched.'
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Promo code panel */}
        {!isActive && (
          <div className="mt-3 p-4 rounded-lg border border-gold/40 bg-gold/5" data-testid="sub-promo-panel">
            {appliedPromo ? (
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <CheckCircle size={18} weight="duotone" className="text-emerald-400" />
                  <div>
                    <p className="text-sm font-serif text-ivory">
                      <span className="text-gold font-medium">{appliedPromo.code}</span> — {appliedPromo.discount_percent === 100 ? `first ${appliedPromo.trial_days} days free` : `${appliedPromo.discount_percent}% off first ${appliedPromo.trial_days} days`}
                    </p>
                    <p className="text-[11px] text-ash">Auto-renews at the standard plan price when the free period ends.</p>
                  </div>
                </div>
                <button
                  onClick={removePromo}
                  className="text-[10px] font-medium uppercase tracking-[0.24em] text-ash hover:text-rose-400"
                  data-testid="remove-sub-promo"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                <Ticket size={16} weight="duotone" className="text-gold shrink-0" />
                <input
                  type="text"
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                  placeholder="Have a promo code? Enter it here"
                  className="flex-1 min-w-[180px] bg-transparent border-b border-border text-ivory font-mono text-sm py-1 focus:outline-none focus:border-gold"
                  data-testid="sub-promo-input"
                />
                <Button
                  onClick={applyPromo}
                  disabled={checking || !promoInput.trim()}
                  className="rounded-md font-medium text-[10px] uppercase tracking-[0.24em] px-3 py-2 bg-gold text-charcoal hover:bg-gold-dark"
                  data-testid="apply-sub-promo"
                >
                  {checking ? 'Checking…' : 'Apply'}
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          {plans.map((plan) => {
            const isCurrent = user?.subscription_tier === plan.id && isActive;
            const covered = promoCoversPlan(plan.id);
            return (
              <div
                key={plan.id}
                className={`p-5 border-2 transition-colors ${
                  isCurrent ? 'border-gold bg-stone' : 'border-border bg-background hover:border-gold'
                }`}
                data-testid={`plan-${plan.id}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Crown size={18} weight="duotone" className="text-gold" />
                  <h3 className="font-medium font-serif text-lg capitalize">{plan.label}</h3>
                </div>
                <p className="text-3xl font-medium font-serif text-gold mb-1">
                  £{plan.monthly_price.toFixed(2)}<span className="text-sm text-ash font-normal">/mo</span>
                </p>
                <p className="text-sm font-medium text-ivory mb-3">{plan.monthly_words.toLocaleString()} words / month</p>
                {appliedPromo && covered && (
                  <p className="text-[11px] font-medium text-emerald-300 mb-3">
                    ✓ {appliedPromo.discount_percent === 100 ? `${appliedPromo.trial_days} days free, then` : `${appliedPromo.discount_percent}% off first ${appliedPromo.trial_days} days, then`} £{plan.monthly_price.toFixed(2)}/mo
                  </p>
                )}
                {appliedPromo && !covered && (
                  <p className="text-[11px] text-ash mb-3">Promo doesn&apos;t apply to this plan.</p>
                )}
                <ul className="space-y-1 mb-4">
                  {plan.features.map((f) => (
                    <li key={f} className="text-xs text-bone font-sans flex items-start gap-1">
                      <span className="text-emerald-400">✓</span> {f}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={isCurrent || isProcessingPayment}
                  className={`w-full rounded-lg font-medium text-xs uppercase tracking-[0.28em] px-4 py-3 transition-colors duration-200 ${
                    isCurrent ? 'bg-stone text-ash cursor-not-allowed' : 'bg-gold text-charcoal hover:bg-gold-dark'
                  }`}
                  data-testid={`subscribe-${plan.id}-btn`}
                >
                  {isCurrent
                    ? 'Current Plan'
                    : appliedPromo && covered
                      ? (appliedPromo.discount_percent === 100 ? 'Start free trial' : 'Subscribe with promo')
                      : 'Subscribe'}
                </Button>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-center text-ash font-sans mt-4">
          True auto-renewing Stripe subscription. Cancel anytime from your billing portal — access continues until the period ends.
        </p>
      </DialogContent>
    </Dialog>
  );
};
